import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormikProps } from 'formik';
import { GridColDef, GridRowOrderChangeParams } from '@mui/x-data-grid-pro';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import { HelpOutline } from '@mui/icons-material';
import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { NumberInput } from '@shared/inputs/NumberInput';
import { TextInput } from '@shared/inputs/TextInput';
import { PhoenixGridHeader } from '@modules/admin/Tools/LenderUtility/shared/PhoenixGridHeader';
import { NumberInputWrapper, RadioWrapper, TooltipWrapper } from '@styles/Admin-tools/DripFeederStyles';
import { CommonButtonWrapper, PageContent } from '@styles/CommonStyles';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { DripFeederGridTypes, IDripFeeder, IDripFeederSource, ISources } from '../../types';

interface IDripFeederProps extends Partial<FormikProps<IDripFeeder>> {
  handleClose: () => void;
  fetchedSources: ISources[] | undefined;
  isEditMode: boolean;
}

type IOtherRows = Pick<IDripFeederSource, 'sourceId' | 'sourceName' | 'isSecondary' | 'isIgnored' | 'isRankedSource'>;

/**
 * Logic for drip feeder sources.

editing/displaying
DFResponse
  - IsRanked = true -> Show under Ranked
  - IsIgnored = true -> Show under Ignored
  - IsRanked = false && IsIgnored = false -> Show under Other
  - Any SourceID from Master list missing in DFResponse -> Show under Ignored (That means this source was created after DripFeeder set was created)

Creating
  - All sources will be under Ignored
  - When saved button is clicked
    -> For sources in Ignored box -> Ignored = true, IsRanked = false
    -> For sources in Other box -> Ignored = false, IsRanked = false
    -> For sources in Ranked box -> Ignored = false, IsRanked = true
 */

const getModifiedObject = (data: IDripFeederSource, type: DripFeederGridTypes) => {
  switch (type) {
    case DripFeederGridTypes.ranked:
      return {
        ...data,
        isRankedSource: true,
        isIgnored: false
      };
    case DripFeederGridTypes.ignored:
      return {
        ...data,
        isRankedSource: false,
        isIgnored: true
      };
    case DripFeederGridTypes.other:
      return {
        ...data,
        isRankedSource: false,
        isIgnored: false
      };
    default:
      return;
  }
};

export const DripFeederForm = ({ setFieldValue, handleClose, values, fetchedSources, isEditMode }: IDripFeederProps) => {

  const [ignoredRows, setIgnoredRows] = useState<Set<IDripFeederSource>>(new Set([]));
  const [otherRows, setOtherRows] = useState<Set<IOtherRows>>(new Set([]));
  const [rankedRows, setRankedRows] = useState<Set<IDripFeederSource>>(
    new Set(
      values?.sources
        ?.filter((item) => item.isRankedSource)
        .map((item) => ({
          ...item,
          id: item.sourceId
        })) || []
    )
  );

  const rankedGridActions = useMemo(() => ({
    onSave: (data: IDripFeederSource) => {
      setRankedRows((prevState) => {
        prevState.forEach((item) => {
          if (item.sourceId === data.sourceId) {
            item.maxPulls = data.maxPulls;
          }
        });
        return new Set(prevState);
      });
    },
  }), []);

  useEffect(() => {
    setFieldValue?.('sources', Array.from(rankedRows)
      .concat(Array.from(ignoredRows), Array.from(otherRows)));
  }, [rankedRows, ignoredRows, otherRows]);

  useEffect(() => {
    if (fetchedSources) {
      const defaultSources = fetchedSources.map((item: ISources) => ({
        sourceId: item.id,
        sourceName: item.name,
        isSecondary: item.isSecondary,
      }));

      if (isEditMode) {
        const rankedSources = values?.sources?.filter((source) => source.isRankedSource)
          .map((item) => (getModifiedObject(item, DripFeederGridTypes.ranked))) ?? [];

        const otherSources = values?.sources?.filter((source) => !source.isIgnored && !source.isRankedSource).
          map((item) => (getModifiedObject(item, DripFeederGridTypes.other))) ?? [];

        const combinedArray = rankedSources?.concat(otherSources).map((item) => item?.sourceId);
        const ignoredSources = defaultSources.filter((source) => !combinedArray?.includes(source.sourceId))
          .map((item) => (getModifiedObject(item, DripFeederGridTypes.ignored))) ?? [];

        setIgnoredRows(new Set(ignoredSources as IDripFeederSource[]));
        setOtherRows(new Set(otherSources as IOtherRows[]));
      } else {
        const ignoredSources = defaultSources.map((item) => (getModifiedObject(item, DripFeederGridTypes.ignored))) ?? [];
        setIgnoredRows(new Set(ignoredSources as IDripFeederSource[]));
      }
    }
  }, [fetchedSources, isEditMode]);

  const handleMoveToRanked = useCallback((row: IOtherRows) => {
    setRankedRows((prevState) => {
      return new Set(prevState).add({
        id: row.sourceId,
        sourceId: row.sourceId,
        sourceName: row.sourceName,
        order: prevState.size + 1,
        isRankedSource: true,
        isIgnored: false,
        maxPulls: 0,
      });
    });

    setOtherRows((prevState) => {
      prevState.delete(row);
      return new Set(prevState);
    });
  }, []);

  const handleMoveToOtherFromIgnore = useCallback((row: IDripFeederSource) => {
    setOtherRows((prevState) =>
      new Set(prevState).add(getModifiedObject(row, DripFeederGridTypes.other) as IDripFeederSource)
    );

    setIgnoredRows((prevState) => {
      prevState.delete(row);
      return new Set(prevState);
    });
  }, [getModifiedObject]);

  const handleMoveToOtherFromRanked = useCallback((row: IDripFeederSource) => {
    setOtherRows((prevState) =>
      new Set(prevState).add(getModifiedObject(row, DripFeederGridTypes.other) as IDripFeederSource)
    );

    setRankedRows((prevState) => {
      prevState.delete(row);
      return new Set(prevState);
    });
  }, [getModifiedObject]);

  const handleMoveToIgnored = useCallback((row: IDripFeederSource) => {
    setIgnoredRows((prevState) => {
      return new Set(prevState).add({
        sourceId: row.sourceId,
        sourceName: row.sourceName,
        order: null,
        isRankedSource: false,
        isIgnored: true,
        maxPulls: 0,
      });
    });

    setOtherRows((prevState) => {
      prevState.delete(row);
      return new Set(prevState);
    });
  }, []);

  const rankedColumns: GridColDef[] = useMemo(() => ([
    dataGridCol('order', {
      headerName: 'Rank',
      flex: 1,
      minWidth: 30,
    }),
    dataGridCol('sourceName', {
      headerName: 'Source name',
      flex: 1,
      minWidth: 100,
    }),
    dataGridCol('maxPulls', {
      minWidth: 100,
      editable: true,
      type: 'number',
    }),
    dataGridCol('arrowRight', {
      headerName: '',
      sortable: false,
      renderCell: (cell) => (
        <ArrowCircleRightIcon
          fontSize="medium"
          onClick={() => handleMoveToOtherFromRanked(cell.row)}
        />
      ),
    }),
  ]), [handleMoveToOtherFromRanked]);

  const otherColumns: GridColDef[] = useMemo(() => ([
    dataGridCol('arrowLeft', {
      maxWidth: 20,
      headerName: '',
      sortable: false,
      renderCell: (cell) => (
        <ArrowCircleLeftIcon
          fontSize="medium"
          onClick={() => handleMoveToRanked(cell.row)}
        />
      ),
    }),
    dataGridCol('sourceName', {
      headerName: 'Source name',
      flex: 1,
    }),
    dataGridCol('arrowRight', {
      maxWidth: 20,
      headerName: '',
      sortable: false,
      renderCell: (cell) => (
        <ArrowCircleRightIcon
          fontSize="medium"
          onClick={() => handleMoveToIgnored(cell.row)}
        />
      ),
    }),
  ]), [handleMoveToIgnored, handleMoveToRanked]);

  const ignoredColumns: GridColDef[] = useMemo(() => ([
    dataGridCol('arrowLeft', {
      maxWidth: 20,
      headerName: '',
      sortable: false,
      renderCell: (cell) => (
        <ArrowCircleLeftIcon
          fontSize="medium"
          onClick={() => handleMoveToOtherFromIgnore(cell.row)}
        />
      ),
    }),
    dataGridCol('sourceName', {
      disableColumnMenu: true,
      flex: 1,
    }),
  ]), [handleMoveToOtherFromIgnore]);

  const handleRowOrderChange = useCallback(async (params: GridRowOrderChangeParams) => {
    if (params.oldIndex !== params.targetIndex) {
      const rankedArrCopy: IDripFeederSource[] = Array.from(rankedRows);

      if (rankedArrCopy) {
        const oldOrder = rankedArrCopy[params.oldIndex].order;

        rankedArrCopy[params.oldIndex].order = rankedArrCopy[params.targetIndex].order;
        rankedArrCopy[params.targetIndex].order = oldOrder;

        setRankedRows(new Set(rankedArrCopy.sort((a, b) => Number(a.order) - Number(b.order))));
      }
    }
  }, [rankedRows]);

  return (
    <>
      <PhoenixGridHeader title="Add / Edit Drip Feeder" />
      <PageContent style={{ minHeight: '300px' }}>
        <Grid
          container
          spacing={12}
        >
          <Grid
            item
            md={12}
          >
            <Stack
              marginBottom={2}
              minWidth="700px"
            >
              <Stack
                display="flex"
                gap={5}
                flexDirection="row"
                alignItems="flex-start"
              >
                <Stack
                  display="flex"
                  gap={3}
                  flex={1}
                  width="50%"
                >
                  <TextInput
                    value={values?.name}
                    label="Name"
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setFieldValue?.('name', event?.target?.value)}
                  />

                  <Stack
                    width="100%"
                    display="flex"
                    gap={3}
                    flexDirection="row"
                  >
                    <NumberInputWrapper>
                      <NumberInput
                        label="Max pulls for set"
                        value={values?.maxPulls}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setFieldValue?.('maxPulls', event.target.value)}
                      />
                    </NumberInputWrapper>

                    <NumberInputWrapper>
                      <NumberInput
                        label="Minutes between pulls"
                        value={values?.minutesBetweenPulls}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          setFieldValue?.('minutesBetweenPulls', event.target.value)
                        }
                      />
                    </NumberInputWrapper>
                  </Stack>
                </Stack>

                <Stack
                  display="flex"
                  flexDirection="row"
                  alignItems="flex-start"
                  flex={1}
                >
                  <RadioWrapper>
                    <FormControl>
                      <FormLabel id="pullNewestFirst-label">
                        <Typography fontWeight="bolder">Lead pull order</Typography>
                      </FormLabel>
                      <RadioGroup
                        aria-labelledby="pullNewestFirst-label"
                        defaultValue={values?.pullNewestFirst}
                        name="pullNewestFirst"
                        onChange={({ target: { value } }) => setFieldValue?.('pullNewestFirst', JSON.parse(value))}
                      >
                        <FormControlLabel
                          value={false}
                          control={<Radio />}
                          label="Oldest lead"
                        />

                        <FormControlLabel
                          value
                          control={<Radio />}
                          label="Newest lead"
                        />
                      </RadioGroup>
                    </FormControl>
                  </RadioWrapper>

                  <RadioWrapper>
                    <FormControl>
                      <FormLabel id="useSourceWithLowestCountFirst-label">
                        <Typography fontWeight="bolder">Source to pull</Typography>
                      </FormLabel>
                      <RadioGroup
                        aria-labelledby="useSourceWithLowestCountFirst-label"
                        defaultValue={values?.useSourceWithLowestCountFirst}
                        name="useSourceWithLowestCountFirst"
                        onChange={({ target: { value } }) => setFieldValue?.('useSourceWithLowestCountFirst', JSON.parse(value))}
                      >
                        <FormControlLabel
                          value
                          control={<Radio />}
                          label="Lowest current pull count"
                        />

                        <FormControlLabel
                          value={false}
                          control={<Radio />}
                          label="Rank order"
                        />
                      </RadioGroup>
                    </FormControl>
                  </RadioWrapper>

                  <FormControlLabel
                    name="isFronterSet"
                    label={<Typography fontWeight={'bold'}>Is Fronter set</Typography>}
                    control={
                      <Checkbox
                        checked={values?.isFronterSet}
                        onChange={(_, checked) => setFieldValue?.('isFronterSet', checked)}
                      />
                    }
                  />
                </Stack>
              </Stack>
            </Stack>

            <Stack
              flexDirection="row"
              justifyContent="space-between"
              mb={5}
              gap={3}
              minHeight="500px"
            >
              <Stack flex={4}>
                <Typography
                  variant="subtitle1"
                  mb={1}
                >
                  Ranked
                </Typography>

                <PhoenixGrid
                  noButton
                  canNotCopy
                  disableSelectionOnClick
                  rowReordering
                  name="Ranked"
                  columns={rankedColumns}
                  actions={rankedGridActions}
                  rows={Array.from(rankedRows) || []}
                  pagination={false}
                  getRowId={(row) => row.sourceId}
                  onRowOrderChange={handleRowOrderChange}
                />
              </Stack>
              <Stack flex={2}>
                <Typography
                  variant="subtitle1"
                  mb={1}
                >
                  Other
                  <TooltipWrapper>
                    <Tooltip
                      title={
                        <>
                          <Typography>
                            - These source are used if no more leads can be pulled from "Ranked" but the user is not at their
                            total max
                          </Typography>

                          <Typography>- A lead will be pulled at random from the list</Typography>
                        </>
                      }
                    >
                      <HelpOutline fontSize="inherit" />
                    </Tooltip>
                  </TooltipWrapper>
                </Typography>
                <PhoenixGrid
                  noButton
                  canNotCopy
                  canNotEdit
                  disableSelectionOnClick
                  name="Other"
                  getRowId={(row) => row.sourceId}
                  columns={otherColumns}
                  rows={Array.from(otherRows) || []}
                  loading={!otherRows}
                  pagination={false}
                />
              </Stack>
              <Stack flex={2}>
                <Typography
                  variant="subtitle1"
                  mb={1}
                >
                  Ignored
                  <TooltipWrapper>
                    <Tooltip title={<Typography>This sources will not be used</Typography>}>
                      <HelpOutline fontSize="inherit" />
                    </Tooltip>
                  </TooltipWrapper>
                </Typography>

                <PhoenixGrid
                  noButton
                  canNotCopy
                  canNotEdit
                  disableSelectionOnClick
                  name="Ignored"
                  columns={ignoredColumns}
                  rows={Array.from(ignoredRows) || []}
                  loading={!ignoredRows}
                  pagination={false}
                  getRowId={(row) => row.sourceId}
                />
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </PageContent>

      <CommonButtonWrapper
        direction="row"
      >
        <CancelButton onClick={handleClose} />
        <SaveButton />
      </CommonButtonWrapper>
    </>
  );
};
